import { SellerPromoCode } from "@/components/order-summary/SellerPromoCode";
import { LiveProvenanceBadge } from "@/components/provenance/LiveProvenanceBadge";
import { useShippingMethodSync } from "@/queries/cart";
import { formatPrice } from "@/utils/money";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { memo, useMemo } from "react";
import { CartFreeShippingMeter } from "../CartFreeShippingMeter";
import { CartLineItem } from "../CartLineItem";
import { ShippingSelector } from "../ShippingSelector";

type StoreSellerCart = SalonStockerHttpTypes.StoreSellerCart;

export interface AccordionContentProps {
  /**
   * The seller cart data
   */
  cart: StoreSellerCart;
  /**
   * ID for accessibility
   */
  contentId: string;
  /**
   * ID of header for aria-labelledby
   */
  headerId: string;
  /**
   * Whether this content is collapsed. Stays mounted either way so an
   * in-progress interaction inside it isn't torn down by collapsing.
   */
  hidden?: boolean;
  /** Currently applied promo code for this cart, if any. */
  appliedPromoCode?: string;
  /** Promo code error message for this cart, if any. */
  promoError?: string;
  /** Apply a promo code to this cart. Supplying it (with onRemovePromo) shows
   * the footer promo input. */
  onApplyPromo?: (cartId: string, code: string) => Promise<void>;
  /** Remove this cart's applied promo code. */
  onRemovePromo?: (cartId: string) => Promise<void>;
}

/**
 * Accordion content component for seller cart.
 * Shows line items, shipping selector, and totals breakdown.
 */
function AccordionContentComponent({
  cart,
  contentId,
  headerId,
  hidden = false,
  appliedPromoCode,
  promoError,
  onApplyPromo,
  onRemovePromo
}: AccordionContentProps) {
  const { totals } = cart;
  const showPromo = Boolean(onApplyPromo && onRemovePromo);

  // Ad hoc lines (KAN-193) - hand-typed, fixed-price items a concierge
  // operator captured from a supplier's own Shopify storefront rather than
  // SalonStocker's catalogue - render in their own labelled section below the
  // normal lines rather than blending into the flat list, so a salon reading
  // their cart can immediately tell "what I quoted through the catalogue"
  // apart from "what my rep added by hand". `item.ad_hoc`'s presence is the
  // only discriminant - see `StoreAdHocLineInfo`'s doc.
  const { normalItems, adHocItems } = useMemo(() => {
    const normal: typeof cart.items = [];
    const adHoc: typeof cart.items = [];
    for (const item of cart.items) {
      (item.ad_hoc ? adHoc : normal).push(item);
    }
    return { normalItems: normal, adHocItems: adHoc };
  }, [cart.items]);

  // The hook lines the persisted method up against the option list - by its
  // composite live-rate id or its bare base id, the same rule the server keeps
  // it by (KAN-108, KAN-230).
  const shippingSync = useShippingMethodSync({
    cartId: cart.cart_id,
    selectedMethod: cart.shipping_methods?.[0],
    rearmAutoSelect: true
  });

  return (
    // One continuous surface. Only three rules, and the count does not grow
    // with the basket: header/body, products/details, and the sum line above
    // the total. Everything else is separated by whitespace - products already
    // carry a thumbnail and price block each, so rules between them add stripes
    // without adding clarity.
    <div
      id={contentId}
      role="region"
      aria-labelledby={headerId}
      hidden={hidden}
      className="border-t border-gray-200"
    >
      {/* Line Items - spacing separates them, no rules */}
      <div className="px-4 py-1">
        {normalItems.map((item) => (
          <CartLineItem
            key={item.id}
            item={item}
            cartId={cart.cart_id}
            // No `sellerName`: the card header already names the supplier, so
            // printing it on every row is noise. The provenance tooltip still
            // wants it, though - "Live price from Hair Co" beside "Live
            // shipping rate from Hair Co" rather than one of them hedging to
            // "the supplier".
            provenanceSource={cart.seller?.name}
            size="md"
            showImage={true}
          />
        ))}
      </div>

      {/* Ad hoc supplier items (KAN-193) - only shown once at least one
          exists. Marked with a pill badge so it reads as a distinct group
          within this supplier's card, not a fourth normal line item. */}
      {adHocItems.length > 0 && (
        <div className="px-4 py-1">
          {adHocItems.map((item) => (
            <CartLineItem
              key={item.id}
              item={item}
              cartId={cart.cart_id}
              provenanceSource={cart.seller?.name}
              label="Added by your SalonStocker rep"
              size="md"
              showImage={true}
            />
          ))}
        </div>
      )}

      {/* Order details - shipping and totals share one zone, held apart by
          space and their own headings rather than another rule. The rule sits
          inside the px-4 gutter rather than on the container, so it matches the
          length of the sum line above Cart Total. Full-bleed is reserved for
          the header boundary, which is a zone change, not a section change. */}
      <div className="px-4 pb-4">
        <div className="space-y-2 border-t border-gray-200 pt-4">
          {/* Subtotal */}
          <div className="flex justify-between text-sm">
            <span className="text-gray-600">Subtotal</span>
            <span className="font-medium text-gray-900">
              {formatPrice(totals.item_subtotal)}
            </span>
          </div>

          {/* Shipping - the row IS the control, same merged-row treatment as
              the order summary: one price, stated once, right-aligned with the
              amounts around it. A separate "Shipping Method" section repeated
              the price this row already shows. `!text-sm !text-gray-900`
              re-scales the inline trigger to match sibling rows.
              `displayAmount` keeps the collapsed value on the cart's own
              `shipping_total` - the figure that sums into Cart Total below -
              rather than the option's list price. */}
          <div className="flex min-w-0 items-center justify-between gap-2 text-sm">
            {/* The marker rides with the label, not the value: the value here
                is a Select trigger that must stay flush right to line up with
                the amounts above it (KAN-105). */}
            <span className="flex items-center gap-1.5 text-gray-600">
              Shipping
              <LiveProvenanceBadge
                provenance={cart.shipping_methods?.[0]?.provenance}
                subject="shipping rate"
                source={cart.seller?.name}
              />
            </span>
            <ShippingSelector
              variant="inline"
              options={shippingSync.options}
              isLoading={shippingSync.isLoading}
              isPending={shippingSync.isPending}
              isSelectionValid={shippingSync.isSelectionValid}
              selectedOptionId={shippingSync.selectedOptionId}
              onSelect={shippingSync.selectOption}
              displayAmount={totals.shipping_total}
              className="!text-sm !text-gray-900"
            />
          </div>

          {/* Discounts */}
          {totals.discount_total > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Discounts</span>
              <span className="font-medium text-green-600">
                -{formatPrice(totals.discount_total)}
              </span>
            </div>
          )}

          {/* Tax */}
          {totals.tax_total > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-gray-600">Tax</span>
              <span className="font-medium text-gray-900">
                {formatPrice(totals.tax_total)}
              </span>
            </div>
          )}
        </div>

        {/* Total - the payoff line, so it keeps a rule and heavier type even
            though the header already shows the same figure. */}
        <div className="mt-3 flex items-center justify-between gap-2 border-t border-gray-200 pt-3">
          <span className="flex min-w-0 items-center gap-1.5 font-semibold text-gray-900">
            Cart Total
            {/* All-live: green only when every price AND the shipping rate
                above were live. Derived server-side, so a card whose lines
                are green but whose shipping fell open reads "Estimated"
                here - which is the whole point of marking the total too. */}
            <LiveProvenanceBadge
              provenance={totals.provenance}
              subject="total"
              source={cart.seller?.name}
            />
          </span>
          <span className="text-xl font-bold text-gray-900">
            {formatPrice(totals.total)}
          </span>
        </div>
      </div>

      {/* Footer strip - per-supplier facts that live with the supplier: the
          free-freight meter and this cart's promo (KAN-75). The meter now owns
          its live region here, since the summary no longer carries a copy. */}
      <div className="flex flex-col gap-2 border-t border-gray-100 bg-gray-50 px-4 py-2 sm:flex-row sm:items-center sm:justify-between sm:gap-3 sm:py-3">
        <div className="min-w-0 flex-1">
          <CartFreeShippingMeter
            cartId={cart.cart_id}
            itemSubtotal={cart.totals.item_subtotal}
            // Composite `base::handle` id for a live rate, so the meter scopes
            // to the selected service instead of falling back to the full list
            // (KAN-108).
            selectedShippingOptionId={shippingSync.selectedOptionId}
          />
        </div>
        {showPromo && onApplyPromo && onRemovePromo && (
          <div className="w-full sm:w-auto sm:flex-shrink-0">
            <SellerPromoCode
              cartId={cart.cart_id}
              appliedCode={appliedPromoCode}
              onApply={onApplyPromo}
              onRemove={onRemovePromo}
              error={promoError}
            />
          </div>
        )}
      </div>
    </div>
  );
}

export const AccordionContent = memo(AccordionContentComponent);
